import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, computed, ElementRef, NgZone, PLATFORM_ID, Renderer2, signal, ViewContainerRef } from '@angular/core';
import { getOffset, isVisible } from '@core/frontend-shared/utils/dom';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UiIconsModule } from '../icons';
import { takeWhileAlive } from '@core/frontend-shared';
import * as i0 from "@angular/core";
const _c0 = ["uiGrid", ""];
const _c1 = ["*"];
/**
 * Inspired by Uikit, the grid component will add classes to columns stating if they are in first row or column.
 * Thanks to that we dont need to use unstable css tricks to fix row/column margins.
 *
 * to match col heights, use .match-height / matchHeight:true.
 * It is recommended to place a ui-card inside each col for best results.
 */
export let UiGridComponent = /*#__PURE__*/(() => {
  class UiGridComponent {
    set uiGrid(input) {
      this.config.set(input);
    }
    constructor(el, target, zone, cd, renderer, ngPlatformId) {
      this.el = el;
      this.target = target;
      this.zone = zone;
      this.cd = cd;
      this.renderer = renderer;
      this.ngPlatformId = ngPlatformId;
      this.clsFirstRow = 'ui-grid-first-row';
      this.clsRowMargin = 'ui-grid-row-margin';
      this.clsFirstCol = 'ui-grid-first-col';
      this.clsMatchHeight = 'match-height';
      this.currentGridState = null;
      this.queueGridUpdate = new BehaviorSubject(null);
      this.config = signal({}, ...(ngDevMode ? [{
        debugName: "config"
      }] : /* istanbul ignore next */[]));
      this.hostClasses = computed(() => {
        const config = this.config();
        const classes = ['p-grid', 'ui-grid'];
        if (config.size) classes.push(`ui-grid-${config.size}`);
        if (config.matchHeight) classes.push(this.clsMatchHeight);
        return classes.join(' ');
      }, ...(ngDevMode ? [{
        debugName: "hostClasses"
      }] : /* istanbul ignore next */[]));
      if (isPlatformBrowser(this.ngPlatformId)) {
        this.zone.runOutsideAngular(() => {
          this.resizeSubscriber = fromEvent(window, 'resize').pipe(debounceTime(100)).subscribe(e => {
            this.queueGridUpdate.next(true);
          });
        });
        this.queueGridUpdate.pipe(takeWhileAlive()).subscribe(() => {
          const state = this.getDomState();
          // console.log('this.stateHasChanges(state)',this.stateHasChanges(state))
          if (this.stateHasChanges(state)) {
            this.updateDomState(state);
          }
        });
      }
    }
    ngAfterViewInit() {
      if (isPlatformBrowser(this.ngPlatformId)) {
        this.queueGridUpdate.next(true);
      }
    }
    ngAfterContentChecked() {
      if (isPlatformBrowser(this.ngPlatformId)) {
        // MUST run outside angular, else the GridUpdater would trigger another content check causing endless looping!
        this.zone.runOutsideAngular(() => {
          this.queueGridUpdate.next(true);
        });
      }
    }
    ngOnDestroy() {
      this.resizeSubscriber?.unsubscribe();
    }
    getDomState() {
      const rows = this.getRows(this.el.nativeElement.children);
      const columns = this.getColumns(rows);
      return {
        rows,
        columns
      };
    }
    stateHasChanges(state) {
      const currState = this.currentGridState;
      if (!currState) return true;
      if (state.columns.length !== currState.columns.length) return true;
      if (state.rows.length !== currState.rows.length) return true;
      for (let i = 0; i < state.rows.length; i++) {
        if (state.rows[i].length !== currState.rows[i].length) return true;
      }
      return false;
    }
    updateDomState({
      columns,
      rows
    }) {
      this.currentGridState = {
        columns,
        rows
      };
      for (let i = 0; i < rows.length; i++) {
        for (let j = 0; j < rows[i].length; j++) {
          if (i === 0) this.renderer.addClass(rows[i][j], this.clsFirstRow);else this.renderer.removeClass(rows[i][j], this.clsFirstRow);
          if (i === 0) this.renderer.removeClass(rows[i][j], this.clsRowMargin);else this.renderer.addClass(rows[i][j], this.clsRowMargin);
          if (j === 0) this.renderer.addClass(rows[i][j], this.clsFirstCol);else this.renderer.removeClass(rows[i][j], this.clsFirstCol);
        }
      }
    }
    getRows(items) {
      return this.sortBy(items, 'top', 'bottom');
    }
    getColumns(rows) {
      const columns = [];
      for (let i = 0; i < rows.length; i++) {
        const sorted = this.sortBy(rows[i], 'left', 'right');
        for (let j = 0; j < sorted.length; j++) {
          columns[j] = !columns[j] ? sorted[j] : columns[j].concat(sorted[j]);
        }
      }
      return columns;
    }
    sortBy(items, startProp, endProp) {
      const sorted = [[]];
      for (let i = 0; i < items.length; i++) {
        const el = items[i];
        if (!isVisible(el)) continue;
        let dim = getOffset(el);
        for (let j = sorted.length - 1; j >= 0; j--) {
          const current = sorted[j];
          if (!current[0]) {
            current.push(el);
            break;
          }
          let startDim;
          if (current[0].offsetParent === el.offsetParent) {
            startDim = getOffset(current[0]);
          } else {
            dim = getOffset(el, true);
            startDim = getOffset(current[0], true);
          }
          if (dim[startProp] >= startDim[endProp] - 1 && dim[startProp] !== startDim[startProp]) {
            sorted.push([el]);
            break;
          }
          if (dim[endProp] - 1 > startDim[startProp] || dim[startProp] === startDim[startProp]) {
            current.push(el);
            break;
          }
          if (j === 0) {
            sorted.unshift([el]);
            break;
          }
        }
      }
      return sorted;
    }
    static {
      this.ɵfac = function UiGridComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UiGridComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(PLATFORM_ID));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: UiGridComponent,
        selectors: [["", "uiGrid", ""], ["ui-grid"]],
        hostVars: 2,
        hostBindings: function UiGridComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassMap(ctx.hostClasses());
          }
        },
        inputs: {
          uiGrid: "uiGrid"
        },
        attrs: _c0,
        ngContentSelectors: _c1,
        decls: 1,
        vars: 0,
        template: function UiGridComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
          }
        },
        dependencies: [UiIconsModule],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return UiGridComponent;
})();